import { ApiError } from '../api';
import { AdminApiError } from '../admin/api';

/** Schnittstelle der Hausverwaltung (verwaltung.<Plattform>). */

export interface TenantOverview {
  id: string;
  slug: string;
  name: string;
  customDomain: string | null;
  status: 'active' | 'suspended';
  house: boolean;
  storageQuotaSeconds: number | null;
  usedSeconds: number;
  courses: number;
  publishedCourses: number;
  customers: number;
  admins: { id: string; email: string; name: string | null; lastLoginAt: string | null }[];
  stripeConnected: boolean;
  createdAt: string;
  scheme: 'light' | 'dark';
}

/** Anfrage über „Kostenlos testen“ auf evercourse.de. */
export interface InquiryView {
  id: string;
  name: string;
  email: string;
  topic: string;
  message: string | null;
  createdAt: string;
  handledAt: string | null;
  note: string | null;
}

export interface PlatformStatusView {
  platform: { tenants: number; customers: number; videosReady: number; videosProcessing: number; videosFailed: number; mailsQueued: number; mailsFailed: number; usedSeconds: number };
  health: { ok: boolean; warnings: string[]; storage: { freeBytes: number; totalBytes: number } | null; workers: Record<string, string> };
  platformDomain: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', 'X-Requested-With': 'fetch', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
  });
  if (!res.ok) {
    let code = String(res.status);
    let message: string | undefined;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      code = data.error ?? code;
      message = data.message;
    } catch {
      /* kein JSON */
    }
    throw new AdminApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export { ApiError };
const json = (data: unknown) => JSON.stringify(data);

export const operatorApi = {
  login: (email: string, password: string) => request<{ operator: { email: string; name: string | null } }>('/api/operator/login', { method: 'POST', body: json({ email, password }) }),
  logout: () => request<{ ok: true }>('/api/operator/logout', { method: 'POST' }),
  me: () => request<{ operator: { email: string; name: string | null } }>('/api/operator/me'),
  changePassword: (currentPassword: string, newPassword: string) => request<{ ok: true }>('/api/operator/password', { method: 'POST', body: json({ currentPassword, newPassword }) }),
  status: () => request<PlatformStatusView>('/api/operator/status'),
  listTenants: () => request<TenantOverview[]>('/api/operator/tenants'),
  getTenant: (id: string) => request<TenantOverview>(`/api/operator/tenants/${id}`),
  createTenant: (input: Record<string, unknown>) => request<{ tenant: TenantOverview; adminPassword: string }>('/api/operator/tenants', { method: 'POST', body: json(input) }),
  updateTenant: (id: string, patch: Record<string, unknown>) => request<TenantOverview>(`/api/operator/tenants/${id}`, { method: 'PATCH', body: json(patch) }),
  resetAdmin: (id: string, email: string, name: string | null) => request<{ password: string; tenant: TenantOverview }>(`/api/operator/tenants/${id}/admins`, { method: 'POST', body: json({ email, name }) }),
  removeAdmin: (id: string, adminId: string) => request<TenantOverview>(`/api/operator/tenants/${id}/admins/${adminId}`, { method: 'DELETE' }),
  listInquiries: () => request<InquiryView[]>('/api/operator/inquiries'),
  updateInquiry: (id: string, patch: { handled?: boolean; note?: string | null }) => request<InquiryView>(`/api/operator/inquiries/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteInquiry: (id: string) => request<{ ok: true }>(`/api/operator/inquiries/${id}`, { method: 'DELETE' }),
};
